/**
 * Note Manager
 *
 * Spawns, updates, and manages note lifecycle
 */

import { Note, NoteData } from '../gameplay/Note';

export class NoteManager {
  private activeNotes: Note[] = [];
  private chartNotes: NoteData[] = [];
  private currentIndex: number = 0;
  private approachTime: number = 1000; // Notes visible for 1 second

  /**
   * Load notes from chart data
   */
  loadChart(notes: NoteData[]): void {
    this.chartNotes = [...notes];
    this.activeNotes = [];
    this.currentIndex = 0;
    console.log(`NoteManager loaded ${notes.length} notes`);
  }

  /**
   * Update note spawning and positions
   */
  update(currentTime: number): void {
    const visualTime = currentTime + this.approachTime;

    // Debug logging (remove after testing)
    if (this.activeNotes.length === 0 && this.currentIndex === 0) {
      console.log(`NoteManager update: currentTime=${currentTime.toFixed(0)}, visualTime=${visualTime.toFixed(0)}, chartNotes=${this.chartNotes.length}`);
    }

    // Spawn new notes that should now be visible
    while (
      this.currentIndex < this.chartNotes.length &&
      this.chartNotes[this.currentIndex].time <= visualTime
    ) {
      const note = new Note(this.chartNotes[this.currentIndex]);
      this.activeNotes.push(note);
      console.log(`Spawned note ${this.currentIndex} at time ${note.time} in lane ${note.lane}`);
      this.currentIndex++;
    }

    // Update all active notes
    for (const note of this.activeNotes) {
      note.update(currentTime, this.approachTime);
    }

    // Remove notes that should despawn
    this.activeNotes = this.activeNotes.filter(
      note => !note.shouldDespawn(currentTime)
    );
  }

  /**
   * Get all active notes
   */
  getActiveNotes(): Note[] {
    return this.activeNotes;
  }

  /**
   * Get notes in a specific lane
   */
  getLaneNotes(lane: number): Note[] {
    return this.activeNotes.filter(note => note.lane === lane);
  }

  /**
   * Remove a specific note (when it's hit)
   */
  removeNote(note: Note): void {
    const index = this.activeNotes.indexOf(note);
    if (index !== -1) {
      this.activeNotes.splice(index, 1);
    }
  }

  /**
   * Reset note manager
   */
  reset(): void {
    this.activeNotes = [];
    this.currentIndex = 0;
  }

  /**
   * Check if all notes have been spawned and cleared
   */
  isComplete(): boolean {
    return this.currentIndex >= this.chartNotes.length && this.activeNotes.length === 0;
  }

  /**
   * Get total note count
   */
  getTotalNotes(): number {
    return this.chartNotes.length;
  }
}
